import apiClient from './apiClient';
import { signupWithPasskey as signupWithPasskeyCeremony } from '@f2m3/shared-api';
import type {
  AuthProvidersResponse,
  AuthTokenResponse,
  User,
} from '../types';
import type { Locale } from '../../i18n/locales';

export interface VerificationStatus {
  email: string;
  email_verified: boolean;
  code_active: boolean;
  retry_after_seconds: number;
}

export interface SendVerificationCodeResult {
  sent: boolean;
  retry_after_seconds?: number;
}

export interface ChangeUnverifiedEmailResult {
  email: string;
  sent: boolean;
  retry_after_seconds: number;
}

const directApiUrl = '/direct/usersvc';

const GUEST_TOKEN_KEY = 'guest_token';

// Persists the trusted-device token returned by a `remember_device` MFA
// verify. Sent back on subsequent password logins so a recognized device can
// skip the TOTP challenge (backend: AuthnController#login_password).
export const TRUSTED_DEVICE_KEY = 'f2m_trusted_device_token';

interface GuestTokenPayload {
  guest_token?: string;
}

const getGuestTokenPayload = (): GuestTokenPayload => {
  const guestToken = localStorage.getItem(GUEST_TOKEN_KEY);
  return guestToken ? { guest_token: guestToken } : {};
};

interface TrustedDevicePayload {
  trusted_device_token?: string;
}

const getTrustedDevicePayload = (): TrustedDevicePayload => {
  const trustedDeviceToken = localStorage.getItem(TRUSTED_DEVICE_KEY);
  return trustedDeviceToken ? { trusted_device_token: trustedDeviceToken } : {};
};

export interface PasswordLoginPayload {
  email: string;
  password: string;
}

export interface FacebookLoginPayload {
  accessToken: string;
}

export interface GoogleLoginPayload {
  credential: string;
}

export interface RegisterPayload {
  email: string;
  name: string;
  password: string;
}

export interface ForgotPasswordPayload {
  email: string;
}

export interface ResetPasswordPayload {
  token: string;
  password: string;
}

export interface UpdateProfilePayload {
  name?: string;
  /** Staff opt-out for the reminder email; omitted keys are left untouched. */
  reminder_emails?: boolean;
}

export interface ChangePasswordPayload {
  currentPassword: string;
  newPassword: string;
  newPasswordConfirmation: string;
}

export interface ExchangeTransferTokenPayload {
  token: string;
}

export interface MfaVerifyPayload {
  mfaToken: string;
  method: 'totp' | 'recovery';
  code: string;
  rememberDevice?: boolean;
}

const userApi = {
  getAuthProviders: ({ email }: { email: string }) =>
    apiClient.get<AuthProvidersResponse>(`${directApiUrl}/auth/providers`, {
      params: { username: email },
    }),

  loginWithPassword: ({ email, password }: PasswordLoginPayload) =>
    apiClient.post<AuthTokenResponse>(`${directApiUrl}/auth/token`, {
      grant_type: 'password',
      username: email,
      password,
      ...getGuestTokenPayload(),
      ...getTrustedDevicePayload(),
    }),

  loginWithFacebook: ({ accessToken }: FacebookLoginPayload) =>
    apiClient.post<AuthTokenResponse>(`${directApiUrl}/auth/facebook/callback`, {
      grant_type: 'facebooktoken',
      facebooktoken: accessToken,
      ...getGuestTokenPayload(),
    }),

  // `credential` is the ID token Google Identity Services hands the browser.
  // The backend verifies its signature and audience itself — nothing here is
  // trusted (AuthnController#login_google).
  loginWithGoogle: ({ credential }: GoogleLoginPayload) =>
    apiClient.post<AuthTokenResponse>(`${directApiUrl}/auth/google/callback`, {
      grant_type: 'googletoken',
      googletoken: credential,
      ...getGuestTokenPayload(),
    }),

  register: ({ email, name, password }: RegisterPayload) =>
    apiClient.post<AuthTokenResponse>(`${directApiUrl}/auth/register`, {
      email,
      name,
      password,
      ...getGuestTokenPayload(),
    }),

  // The shop is identified by request origin (backend:
  // AuthnController#forgot_password), so — unlike adminapp — no subdomain is
  // sent. Always resolves, even for an unknown address: the backend answers ok
  // either way so a caller cannot enumerate accounts.
  forgotPassword: ({ email }: ForgotPasswordPayload) =>
    apiClient.post<void>(`${directApiUrl}/auth/forgot-password`, { email }),

  resetPassword: ({ token, password }: ResetPasswordPayload) =>
    apiClient.post<void>(`${directApiUrl}/auth/reset-password`, { token, password }),

  verifyEmail: (token: string) =>
    apiClient.post<void>(`${directApiUrl}/auth/verify-email`, { token }),

  // The shop is identified by request origin, as for forgotPassword. Always
  // resolves: the backend answers ok whether or not the address has an account.
  resendVerification: (email: string) =>
    apiClient.post<void>(`${directApiUrl}/auth/resend-verification`, { email }),

  verificationStatus: () => apiClient.get<VerificationStatus>(`${directApiUrl}/auth/verification-status`),

  sendVerificationCode: () =>
    apiClient.post<SendVerificationCodeResult>(`${directApiUrl}/auth/send-verification-code`),

  verifyEmailCode: (code: string) =>
    apiClient.post<{ email_verified: boolean }>(`${directApiUrl}/auth/verify-email-code`, { code }),

  changeUnverifiedEmail: (email: string) =>
    apiClient.post<ChangeUnverifiedEmailResult>(`${directApiUrl}/auth/change-unverified-email`, { email }),

  signupWithPasskey: ({ email, name }: { email: string; name: string }) =>
    signupWithPasskeyCeremony<AuthTokenResponse>(apiClient, {
      email,
      name,
      finishExtra: getGuestTokenPayload() as Record<string, unknown>,
    }),

  refreshToken: () => apiClient.post<AuthTokenResponse>(`${directApiUrl}/auth/refresh`),

  startImpersonation: ({ user_id }: { user_id: string }) =>
    apiClient.post<{
      impersonation: {
        target_id: string;
        target_name: string;
        target_email: string;
        admin_name: string;
        expires_at: string;
      };
    }>(`${directApiUrl}/auth/impersonate`, { user_id }),

  stopImpersonation: () =>
    apiClient.post<{ user_id: string; account_id: string }>(`${directApiUrl}/auth/impersonate/stop`),

  getMe: () => apiClient.get<User>(`${directApiUrl}/users/me`),

  // Body key is `preferred_locale`, not `locale`: apiClient appends an ambient
  // `?locale=<current UI language>` to every request (docs/TRANSLATIONS.md),
  // and Rails merges query params OVER body params — a body key of `locale`
  // would always be shadowed by that query param, so the endpoint could never
  // store a language different from whatever the UI happened to be in when
  // the request was built. See user-service's UsersController#update_locale.
  setLocale: (locale: Locale) =>
    apiClient.put<{ locale: Locale }>(`${directApiUrl}/users/me/locale`, { preferred_locale: locale }),

  // Only the keys the caller actually set are sent: `users#update` permits
  // both, and a stray `name: undefined` would serialize as null and blank
  // the user's name.
  updateMe: (payload: UpdateProfilePayload) =>
    apiClient.put<User>(`${directApiUrl}/users/me`, {
      ...(payload.name !== undefined ? { name: payload.name } : {}),
      ...(payload.reminder_emails !== undefined ? { reminder_emails: payload.reminder_emails } : {}),
    }),

  changePassword: ({
    currentPassword,
    newPassword,
    newPasswordConfirmation,
  }: ChangePasswordPayload) =>
    apiClient.put<AuthTokenResponse>(`${directApiUrl}/users/me/password`, {
      current_password: currentPassword,
      new_password: newPassword,
      new_password_confirmation: newPasswordConfirmation,
    }),

  exchangeTransferToken: ({ token }: ExchangeTransferTokenPayload) =>
    apiClient.post<AuthTokenResponse>(`${directApiUrl}/auth/transfer`, { token }),

  verifyMfa: async ({ mfaToken, method, code, rememberDevice }: MfaVerifyPayload) => {
    const resp = await apiClient.post<AuthTokenResponse>(`${directApiUrl}/auth/mfa/verify`, {
      mfa_token: mfaToken,
      method,
      code,
      remember_device: rememberDevice ?? false,
      ...getGuestTokenPayload(),
    });
    if (resp.trusted_device_token) localStorage.setItem(TRUSTED_DEVICE_KEY, resp.trusted_device_token);
    return resp;
  },
};

export default userApi;
